import React, { useEffect, useState } from 'react';
import { LopButton } from '../../components/LopButton';
import { KeyRound } from 'lucide-react';
import {
  createImportDef,
  deleteImportDef,
  getImportDef,
  getImportTargetSchema,
  getImportTargets,
  updateImportDef,
} from '../../api/imports';
import type {
  ExistingMode,
  ImportColumn,
  ImportDefSummary,
  ImportMapping,
  ImportScope,
  ImportTarget,
} from '../../types/Import';
import { t } from '../../i18n/t';
import { fieldLabel } from './fieldDictionary';

interface ImportDefinePanelProps {
  defs: ImportDefSummary[];
  onChanged: () => void;
}

// One editable row per target-table column.
//   mode 'file'     => value comes from `sourceColumn` (excluded if empty).
//   mode 'constant' => `constantValue` applied to every row.
//   mode 'none'     => column left unmapped (only for non-required columns).
interface MapRow {
  column: ImportColumn;
  isKey: boolean;
  sourceColumn: string;
  mode: 'file' | 'constant' | 'none';
  constantValue: string;
}

interface EditorState {
  // null importDefId => creating a new program.
  importDefId: number | null;
  importName: string;
  targetTable: string;
  targetScope: ImportScope;
  defaultExistingMode: ExistingMode;
  isActive: boolean;
  rows: MapRow[];
}

function emptyEditor(): EditorState {
  return {
    importDefId: null,
    importName: '',
    targetTable: '',
    targetScope: 'Tenant',
    defaultExistingMode: 'Skip',
    isActive: true,
    rows: [],
  };
}

function buildRows(
  columns: ImportColumn[],
  existing?: Map<string, ImportMapping>,
): MapRow[] {
  return columns.map((column) => {
    const prev = existing?.get(column.columnName);
    if (prev) {
      return {
        column,
        isKey: prev.isKey,
        sourceColumn: prev.sourceColumn,
        mode: prev.valueSource === 'Constant' ? 'constant' : 'file',
        constantValue: prev.constantValue ?? '',
      };
    }
    // Default every column to 'file' (an empty source box = excluded, same as the
    // original behavior). The user opts a column into 'constant' or 'none'
    // explicitly; 'none' is disabled for required columns in the select.
    return {
      column,
      isKey: column.isPrimaryKey,
      sourceColumn: '',
      mode: 'file',
      constantValue: '',
    };
  });
}

export function ImportDefinePanel({ defs, onChanged }: ImportDefinePanelProps) {
  const [targets, setTargets] = useState<ImportTarget[]>([]);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getImportTargets()
      .then((data) => {
        if (!cancelled) setTargets(data);
      })
      .catch(() => {
        if (!cancelled) setTargets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function targetLabel(tableName: string): string {
    return (
      targets.find((tg) => tg.tableName === tableName)?.displayName ?? tableName
    );
  }

  async function startNew() {
    setEditorError(null);
    setEditor(emptyEditor());
  }

  async function startEdit(id: number) {
    setEditorError(null);
    try {
      const def = await getImportDef(id);
      const columns = await getImportTargetSchema(def.config.targetTable);
      const existing = new Map<string, ImportMapping>(
        def.config.mappings.map((m) => [
          m.column,
          {
            ...m,
            valueSource: m.valueSource ?? 'File',
            constantValue: m.constantValue ?? '',
          },
        ]),
      );
      setEditor({
        importDefId: def.importDefId,
        importName: def.importName,
        targetTable: def.config.targetTable,
        targetScope: def.config.targetScope,
        defaultExistingMode: def.config.defaultExistingMode,
        isActive: def.isActive,
        rows: buildRows(columns, existing),
      });
    } catch (e: unknown) {
      setEditorError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleTableChange(tableName: string) {
    if (!editor) return;
    if (tableName === '') {
      setEditor({ ...editor, targetTable: '', targetScope: 'Tenant', rows: [] });
      return;
    }
    const target = targets.find((tg) => tg.tableName === tableName);
    try {
      const columns = await getImportTargetSchema(tableName);
      setEditor({
        ...editor,
        targetTable: tableName,
        targetScope: target?.scope ?? 'Tenant',
        rows: buildRows(columns),
      });
    } catch (e: unknown) {
      setEditorError(e instanceof Error ? e.message : String(e));
    }
  }

  function updateRow(index: number, patch: Partial<MapRow>) {
    if (!editor) return;
    const rows = editor.rows.map((r, i) =>
      i === index ? { ...r, ...patch } : r,
    );
    setEditor({ ...editor, rows });
  }

  function cancelEditor() {
    setEditor(null);
    setEditorError(null);
  }

  async function handleSave() {
    if (!editor) return;
    setEditorError(null);

    if (editor.importName.trim() === '') {
      setEditorError(t('imports.define.nameRequired'));
      return;
    }
    if (editor.targetTable === '') {
      setEditorError(t('imports.define.tableRequired'));
      return;
    }
    const mappings = editor.rows
      .filter(
        (r) =>
          r.mode === 'constant' ||
          (r.mode === 'file' && r.sourceColumn.trim() !== ''),
      )
      .map((r) =>
        r.mode === 'constant'
          ? {
              column: r.column.columnName,
              sourceColumn: '',
              isKey: r.isKey,
              valueSource: 'Constant' as const,
              constantValue: r.constantValue,
            }
          : {
              column: r.column.columnName,
              sourceColumn: r.sourceColumn.trim(),
              isKey: r.isKey,
              valueSource: 'File' as const,
              constantValue: null,
            },
      );
    if (mappings.length === 0) {
      setEditorError(t('imports.define.noMappings'));
      return;
    }

    const dto = {
      importName: editor.importName.trim(),
      isActive: editor.isActive,
      config: {
        targetTable: editor.targetTable,
        targetScope: editor.targetScope,
        defaultExistingMode: editor.defaultExistingMode,
        mappings,
      },
    };

    setSaving(true);
    try {
      if (editor.importDefId === null) {
        await createImportDef(dto);
      } else {
        await updateImportDef(editor.importDefId, dto);
      }
      setEditor(null);
      onChanged();
    } catch (e: unknown) {
      setEditorError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editor || editor.importDefId === null) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('imports.define.confirmDelete'))) return;
    setSaving(true);
    setEditorError(null);
    try {
      await deleteImportDef(editor.importDefId);
      setEditor(null);
      onChanged();
    } catch (e: unknown) {
      setEditorError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="gimport-section">
      <h2 className="gimport-section__title">{t('imports.define.title')}</h2>

      <div className="gimport-proglist">
        {defs.length === 0 && (
          <span className="gimport-muted">{t('imports.define.noPrograms')}</span>
        )}
        {defs.map((d) => (
          <div className="gimport-progrow" key={d.importDefId}>
            <span className="gimport-progrow__name">{d.importName}</span>
            <span className="gimport-progrow__meta">
              {targetLabel(d.targetTable)}
            </span>
            <span
              className={
                d.isActive
                  ? 'gimport-badge gimport-badge--active'
                  : 'gimport-badge gimport-badge--inactive'
              }
            >
              {d.isActive
                ? t('imports.define.active')
                : t('imports.define.inactive')}
            </span>
            <span className="gimport-progrow__spacer" />
            <LopButton variant="secondary" onClick={() => startEdit(d.importDefId)}>
              {t('imports.define.edit')}
            </LopButton>
          </div>
        ))}
      </div>

      {editor === null && (
        <LopButton variant="primary" onClick={startNew}>
          {t('imports.define.newProgram')}
        </LopButton>
      )}

      {editor !== null && (
        <div className="gimport-editor">
          <h3 className="gimport-editor__title">
            {editor.importDefId === null
              ? t('imports.define.editorNewTitle')
              : t('imports.define.editorEditTitle')}
          </h3>

          <div className="gimport-field">
            <label
              className="gimport-field__label"
              htmlFor="gimport-def-name"
            >
              {t('imports.define.name')}
              <span
                className="gimport-field__required"
                title={t('imports.define.requiredMark')}
              >
                *
              </span>
            </label>
            <input
              id="gimport-def-name"
              className="gimport-input"
              type="text"
              value={editor.importName}
              onChange={(e) =>
                setEditor({ ...editor, importName: e.target.value })
              }
            />
          </div>

          <div className="gimport-field">
            <label
              className="gimport-field__label"
              htmlFor="gimport-def-table"
            >
              {t('imports.define.targetTable')}
              <span
                className="gimport-field__required"
                title={t('imports.define.requiredMark')}
              >
                *
              </span>
            </label>
            <select
              id="gimport-def-table"
              className="gimport-select"
              value={editor.targetTable}
              onChange={(e) => handleTableChange(e.target.value)}
            >
              <option value="" disabled>
                {t('imports.define.selectTable')}
              </option>
              {targets.map((tg) => (
                <option key={tg.tableName} value={tg.tableName}>
                  {tg.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="gimport-field">
            <label
              className="gimport-field__label"
              htmlFor="gimport-def-mode"
            >
              {t('imports.define.defaultMode')}
            </label>
            <select
              id="gimport-def-mode"
              className="gimport-select"
              value={editor.defaultExistingMode}
              onChange={(e) =>
                setEditor({
                  ...editor,
                  defaultExistingMode: e.target.value as ExistingMode,
                })
              }
            >
              <option value="Skip">{t('imports.run.modeSkip')}</option>
              <option value="Update">{t('imports.run.modeUpdate')}</option>
              <option
                value="UpdateOnly"
                title={t('imports.run.modeUpdateOnlyHint')}
              >
                {t('imports.run.modeUpdateOnly')}
              </option>
            </select>
          </div>

          <label className="gimport-checkbox">
            <input
              type="checkbox"
              checked={editor.isActive}
              onChange={(e) =>
                setEditor({ ...editor, isActive: e.target.checked })
              }
            />
            {t('imports.define.active')}
          </label>

          {editor.rows.length > 0 && (
            <>
              <h4 className="gimport-field__label">
                {t('imports.define.columns')}
              </h4>
              <p className="gimport-muted">{t('imports.define.columnsHint')}</p>
              <table className="gimport-table">
                <thead>
                  <tr>
                    <th className="gimport-map-key" />
                    <th>{t('imports.define.colTarget')}</th>
                    <th className="gimport-map-key">
                      {t('imports.define.colKey')}
                    </th>
                    <th>{t('imports.define.source')}</th>
                    <th>{t('imports.define.colValue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {editor.rows.map((row, index) => {
                    const required =
                      row.column.isPrimaryKey ||
                      (!row.column.isNullable && !row.column.isIdentity);
                    const dictLabel = fieldLabel(
                      editor.targetTable,
                      row.column.columnName,
                    );
                    return (
                      <tr key={row.column.columnName}>
                        <td className="gimport-map-key">
                          {row.column.isPrimaryKey && (
                            <KeyRound
                              size={14}
                              className="gimport-key-icon"
                              aria-label={t('imports.define.keyTitle')}
                            />
                          )}
                        </td>
                        <td className="gimport-map-target">
                          {row.column.columnName}
                          {required && (
                            <span
                              className="gimport-field__required"
                              title={t('imports.define.requiredMark')}
                            >
                              {' *'}
                            </span>
                          )}
                          {dictLabel !== undefined && (
                            <span className="gimport-map-target__dict">
                              {' · '}
                              {dictLabel}
                            </span>
                          )}
                          <span className="gimport-map-target__type">
                            ({row.column.dataType})
                          </span>
                        </td>
                        <td className="gimport-map-key">
                          <input
                            type="checkbox"
                            checked={row.isKey}
                            aria-label={t('imports.define.colKey')}
                            onChange={(e) =>
                              updateRow(index, { isKey: e.target.checked })
                            }
                          />
                        </td>
                        <td className="gimport-map-mode">
                          <select
                            className="gimport-select"
                            value={row.mode}
                            aria-label={t('imports.define.source')}
                            onChange={(e) =>
                              updateRow(index, {
                                mode: e.target.value as MapRow['mode'],
                              })
                            }
                          >
                            <option value="file">
                              {t('imports.define.sourceFile')}
                            </option>
                            <option value="constant">
                              {t('imports.define.sourceConstant')}
                            </option>
                            <option value="none" disabled={required}>
                              {t('imports.define.sourceNone')}
                            </option>
                          </select>
                        </td>
                        <td className="gimport-map-source">
                          {row.mode === 'file' && (
                            <input
                              className="gimport-input"
                              type="text"
                              value={row.sourceColumn}
                              aria-label={t('imports.define.colSource')}
                              onChange={(e) =>
                                updateRow(index, {
                                  sourceColumn: e.target.value,
                                })
                              }
                            />
                          )}
                          {row.mode === 'constant' && (
                            <input
                              className="gimport-input"
                              type="text"
                              value={row.constantValue}
                              aria-label={t('imports.define.colConstant')}
                              placeholder={t(
                                'imports.define.constantPlaceholder',
                              )}
                              onChange={(e) =>
                                updateRow(index, {
                                  constantValue: e.target.value,
                                })
                              }
                            />
                          )}
                          {row.mode === 'none' && (
                            <span className="gimport-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {editorError !== null && (
            <div className="gimport-inline-error" role="alert">
              {editorError}
            </div>
          )}

          <div className="gimport-actions">
            <LopButton variant="primary" disabled={saving} onClick={handleSave}>
              {t('imports.define.save')}
            </LopButton>
            <LopButton variant="secondary" disabled={saving} onClick={cancelEditor}>
              {t('imports.define.cancel')}
            </LopButton>
            {editor.importDefId !== null && (
              <LopButton variant="danger" disabled={saving} onClick={handleDelete}>
                {t('imports.define.delete')}
              </LopButton>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
